<template>
  <div class="flex-stretch">
    <Placeholder v-if="!initialized"></Placeholder>
    <template v-else>
      <NoteContainer class="flex-stretch" v-if="activeViewMode.name === 'note'">
        <template v-slot:breadcrumb>
          <NoteBreadcrumb :note="currentNote"></NoteBreadcrumb>
        </template>
        <template v-slot:title>
          <NoteTitle :note="currentNote" @update="updateNoteTitle"></NoteTitle>
        </template>
        <template v-slot:toolbar>
          <NoteToolbar
            @set-mode="setNoteMode"
            @delete="deleteNote"
            @set-root="setRootNote"
            :active-mode="noteMode"
            :note-can-be-deleted="noteCanBeDeleted"
            :note-is-root="currentNote.isRoot"
          ></NoteToolbar>
        </template>
        <template v-slot:content>
          <note-content-editor
            :content="currentNote.content"
            :note="currentNote"
            @update="updateNoteContent"
            v-if="noteMode === 'rich'"
            class="flex-stretch"
          ></note-content-editor>
          <note-content-markdown-editor
            :content="currentNote.content"
            @update="updateNoteContent"
            v-if="noteMode === 'md'"
            class="flex-stretch"
          ></note-content-markdown-editor>
        </template>
        <template v-slot:footer>
          <References
            v-if="currentNote.links.from.length > 0 && noteMode === 'rich'"
            :currentNote="currentNote"
          ></References>
          <p class="text-muted mb-0 text-right last-modified">
            Last modified {{ lastModified }}
          </p>
        </template>
      </NoteContainer>
      <NoteList v-else-if="activeViewMode.name === 'list'"></NoteList>
      <Mindmap
        v-else-if="activeViewMode.name === 'mindmap'"
        :note="currentNote"
      ></Mindmap>
    </template>
  </div>
</template>
<script>
import Placeholder from '@/components/Placeholder'
import NoteContainer from '@/components/Note/NoteContainer'
import NoteBreadcrumb from '@/components/Note/NoteBreadcrumb'
import References from '@/components/Note/References'
import NoteTitle from '@/components/Note/NoteTitle'
import NoteContentEditor from '@/components/Note/NoteContentEditor'
import NoteContentMarkdownEditor from '@/components/Note/NoteContentMarkdownEditor'
import NoteToolbar from '@/components/Note/NoteToolbar'
import NoteList from '@/views/NoteList'
import Mindmap from '@/views/Mindmap'
import goToNote from '@/mixins/go-to-note'
import { mapState, mapGetters } from 'vuex'
import { trackEvent } from '@/utils/analytics'
import { distanceInWords, isToday, format } from 'date-fns'

export default {
  props: ['contextion', 'viewMode'],
  mixins: [goToNote],
  components: {
    Placeholder,
    References,
    NoteContainer,
    NoteBreadcrumb,
    NoteTitle,
    NoteContentEditor,
    NoteContentMarkdownEditor,
    NoteToolbar,
    NoteList,
    Mindmap
  },
  data() {
    return {
      noteMode: 'rich'
    }
  },
  computed: {
    ...mapState(['user', 'initialized', 'activeViewMode']),
    ...mapGetters(['currentNote', 'breadcrumb', 'rootNote', 'getNoteById']),
    noteCanBeDeleted() {
      return !this.currentNote.isRoot
    },
    promptForPassphrase() {
      return this.$store.getters['encryption/promptForPassphrase']
    },
    lastModified() {
      const timestamp = this.currentNote.modified
      const dateDistanceInWords = distanceInWords(
        new Date(timestamp),
        new Date()
      ) + ' ago'
      return isToday(timestamp) ? dateDistanceInWords : format(timestamp, 'D MMMM [at] HH:mm ')
    }
  },
  created() {
    this.$store.dispatch('setViewMode', this.viewMode)
  },
  methods: {
    deleteNote() {
      const breadcrumb = this.breadcrumb
      this.$store.dispatch('deleteNote', this.currentNote)
      if (breadcrumb.length > 1) {
        const newBreadcrumb = breadcrumb.slice()
        newBreadcrumb.pop()
        this.goToNote(newBreadcrumb)
      } else {
        this.$router.push({ name: 'rootNote' })
      }
    },
    updateNoteTitle(title) {
      this.$store.dispatch('updateNote', {
        ...this.currentNote,
        title
      })
    },
    updateNoteContent(content) {
      this.$store.dispatch('updateNote', {
        ...this.currentNote,
        content
      })
    },
    setNoteMode(noteMode) {
      this.noteMode = noteMode
    },
    setRootNote() {
      trackEvent('set note as root', {
        eventCategory: 'note',
        eventAction: 'set_root'
      })
      this.$store.dispatch('setRootNote', this.currentNote)
    }
  },
  watch: {
    initialized() {
      if (this.initialized && !this.currentNote) this.$router.push({ name: 'rootNote' })
    }
  }
}
</script>
<style scoped>
.last-modified {
  font-weight: 300;
}
</style>
